import { type ReactElement, forwardRef } from 'react'
import type { CanvasPosition, TileSize } from '../../../../types/infrastructure-map'
import { COLLECTION_BADGE_PATH, type TileSizeConfig, iconSizeClass } from './tile-node.helpers'

interface DotTileProps {
  endpointId: string
  hostname: string
  ipAddress: string
  position: CanvasPosition
  borderClass: string
  isSelected: boolean
  isLinkSource: boolean
  isDragging: boolean
  onMouseDown: (event: React.MouseEvent) => void
  onContextMenu: (event: React.MouseEvent) => void
  onClick: (event: React.MouseEvent) => void
}

export const DotTile = forwardRef<HTMLDivElement, DotTileProps>(
  function DotTile(props, ref): ReactElement {
    const {
      endpointId,
      hostname,
      ipAddress,
      position,
      borderClass,
      isSelected,
      isLinkSource,
      isDragging,
      onMouseDown,
      onContextMenu,
      onClick,
    } = props
    const dotClass = isSelected
      ? 'ring-2 ring-fuchsia-500 ring-offset-1 bg-fuchsia-100 dark:bg-fuchsia-900'
      : 'bg-white dark:bg-slate-800'
    const linkRing = isLinkSource ? 'ring-2 ring-violet-500 ring-offset-1' : ''
    return (
      <div
        ref={ref}
        data-node="true"
        data-endpoint-id={endpointId}
        className={`absolute cursor-grab transition-shadow active:cursor-grabbing ${isDragging ? 'z-50' : 'z-10'}`}
        style={{
          left: position.x,
          top: position.y,
          transform: 'translate(-50%, -50%)',
          pointerEvents: 'auto',
        }}
        onMouseDown={onMouseDown}
        onContextMenu={onContextMenu}
        onClick={onClick}
        title={`${hostname}\n${ipAddress}`}
      >
        <div
          className={`h-4 w-4 rounded-full border-2 ${borderClass} ${dotClass} ${linkRing} transition-transform hover:scale-125`}
        />
      </div>
    )
  }
)

interface FullTileProps {
  endpointId: string
  hostname: string
  ipAddress: string
  deviceType: string
  role?: string | undefined
  tileSize: TileSize
  borderClass: string
  icon: string
  position: CanvasPosition
  config: TileSizeConfig
  isSelected: boolean
  isLinkSource: boolean
  isDragging: boolean
  isInCollectionSet: boolean
  collectionTitle: string
  onMouseDown: (event: React.MouseEvent) => void
  onContextMenu: (event: React.MouseEvent) => void
  onClick: (event: React.MouseEvent) => void
}

export const FullTile = forwardRef<HTMLDivElement, FullTileProps>(
  function FullTile(props, ref): ReactElement {
    const {
      endpointId,
      hostname,
      ipAddress,
      deviceType,
      role,
      tileSize,
      borderClass,
      icon,
      position,
      config,
      isSelected,
      isLinkSource,
      isDragging,
      isInCollectionSet,
      collectionTitle,
      onMouseDown,
      onContextMenu,
      onClick,
    } = props
    return (
      <div
        ref={ref}
        data-node="true"
        data-endpoint-id={endpointId}
        className={`absolute cursor-grab transition-all active:cursor-grabbing ${isDragging ? 'z-50 shadow-xl' : 'z-10'}`}
        style={{
          left: position.x,
          top: position.y,
          transform: 'translate(-50%, -50%)',
          width: config.width,
          height: config.height,
          pointerEvents: 'auto',
        }}
        onMouseDown={onMouseDown}
        onContextMenu={onContextMenu}
        onClick={onClick}
      >
        <TileBody
          borderClass={borderClass}
          isSelected={isSelected}
          isLinkSource={isLinkSource}
          isDragging={isDragging}
        >
          <span className={iconSizeClass(tileSize)}>{icon}</span>
          <span
            className={`${config.textClass} mt-1 max-w-[90%] truncate font-semibold text-slate-900 dark:text-slate-100`}
          >
            {hostname}
          </span>
          {(tileSize === 'medium' || tileSize === 'large') && (
            <span className="max-w-[90%] truncate font-mono text-xs text-slate-500 dark:text-slate-400">
              {ipAddress}
            </span>
          )}
          {tileSize === 'large' && (
            <span className="mt-0.5 max-w-[90%] truncate text-[10px] text-slate-400 capitalize dark:text-slate-500">
              {deviceType.replaceAll('-', ' ')}
            </span>
          )}
          {role && tileSize === 'large' && (
            <span className="max-w-[90%] truncate text-[10px] text-fuchsia-500 dark:text-fuchsia-400">
              {role}
            </span>
          )}
          {isInCollectionSet && <CollectionBadge title={collectionTitle} />}
        </TileBody>
      </div>
    )
  }
)

interface TileBodyProps {
  borderClass: string
  isSelected: boolean
  isLinkSource: boolean
  isDragging: boolean
  children: React.ReactNode
}

function TileBody({
  borderClass,
  isSelected,
  isLinkSource,
  isDragging,
  children,
}: TileBodyProps): ReactElement {
  const selectedClass = isSelected
    ? 'ring-2 ring-fuchsia-500 ring-offset-2 shadow-lg shadow-fuchsia-500/20'
    : 'hover:shadow-md hover:border-fuchsia-300 dark:hover:border-fuchsia-700'
  const linkClass = isLinkSource ? 'ring-2 ring-violet-500 ring-offset-2' : ''
  const shadowClass = isDragging ? 'shadow-xl' : 'shadow-sm'
  return (
    <div
      className={`flex h-full w-full flex-col items-center justify-center rounded-xl border-2 ${borderClass} bg-white transition-all duration-150 dark:bg-slate-800 ${selectedClass} ${linkClass} ${shadowClass}`}
    >
      {children}
    </div>
  )
}

function CollectionBadge({ title }: { title: string }): ReactElement {
  return (
    <div
      className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-emerald-500 shadow-sm"
      title={title}
    >
      <svg className="h-3 w-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d={COLLECTION_BADGE_PATH}
        />
      </svg>
    </div>
  )
}
